import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, formatAxiosError, fileUrl, providerDocHref } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Package, Plus, Pencil, Archive, ArchiveRestore, X, UserPlus, Upload, FileText, Link2, ExternalLink, Languages } from "lucide-react";
import TranslationDialog from "../components/TranslationDialog";

/** Bouton « Anglais » (version présentée aux clients anglophones) ; `done` : déjà traduit. */
const EnButton = ({ onClick, done, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} title="Version anglaise (clients anglophones)"
    className={`p-1.5 transition-colors ${done ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
    <Languages className="w-4 h-4" />
  </button>
);

const DOCUMENT_TYPES = { kbis: "Extrait K-bis", piece_identite: "Pièce d'identité", rib: "RIB", autre: "Autre document" };
const CLIENT_TYPES = [
  { value: "pro", label: "Pro" },
  { value: "tpe", label: "Pro ≤ 5 salariés" },
  { value: "particulier", label: "Particulier" },
];
// K-bis non exigé par défaut : le SIRET du client pro vient de l'annuaire des entreprises (reste déposable, et cochable ici).
const DEFAULT_REQUIRED_DOCUMENTS = {
  pro: ["piece_identite", "rib"],
  tpe: ["piece_identite", "rib"],
  particulier: ["piece_identite", "rib"],
};

const commitmentSummary = (opts) => {
  if (!opts || opts.length === 0) return "Sans engagement";
  const first = opts[0];
  const label = first.months === 0 ? "Sans engagement" : `Engagement ${first.months} mois`;
  return opts.length > 1 ? `${label} (+${opts.length - 1})` : label;
};

export default function Catalog() {
  const { role, isSuperadmin } = useAuth();
  const canManage = ["owner", "admin"].includes(role) || isSuperadmin;
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState(null); // null = chargement
  const [forbidden, setForbidden] = useState(false);

  const loadProducts = () =>
    api.get("/catalog/products", { params: { include_archived: 1 } })
      .then((r) => setProducts(r.data))
      .catch((e) => {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error(formatAxiosError(e));
      });

  useEffect(() => { loadProducts(); }, []);

  if (forbidden) {
    return (
      <div className="p-6 md:p-8 lg:p-12 max-w-3xl">
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center" data-testid="catalog-forbidden">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Le portail partenaires est inclus dans le forfait Enterprise.</p>
        </div>
      </div>
    );
  }

  if (products === null) {
    return <div className="p-12 text-muted-foreground">Chargement…</div>;
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Portail partenaires</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Catalogue</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto" data-testid="catalog-tabs">
          <TabsTrigger value="products" data-testid="catalog-tab-products">Produits</TabsTrigger>
          <TabsTrigger value="partners" data-testid="catalog-tab-partners">Partenaires</TabsTrigger>
          <TabsTrigger value="templates" data-testid="catalog-tab-templates">Modèles partenaires</TabsTrigger>
          <TabsTrigger value="dossier" data-testid="catalog-tab-dossier">Dossier client</TabsTrigger>
          <TabsTrigger value="channels" data-testid="catalog-tab-channels">Chaînes TV</TabsTrigger>
          <TabsTrigger value="providers" data-testid="catalog-tab-providers">Prestataires</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <ProductsTab products={products} reload={loadProducts} canManage={canManage} />
        </TabsContent>
        <TabsContent value="partners">
          <PartnersTab products={products} canManage={canManage} />
        </TabsContent>
        <TabsContent value="templates">
          <PartnerTemplatesTab products={products} canManage={canManage} />
        </TabsContent>
        <TabsContent value="dossier">
          <DossierTab canManage={canManage} />
        </TabsContent>
        <TabsContent value="channels">
          <ChannelsTab canManage={canManage} onOpenProviders={() => setTab("providers")} />
        </TabsContent>
        <TabsContent value="providers">
          <ProvidersTab canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────── Produits ───────────────────────────

/** Réglage société : les générations IA ne choisissent que dans ce catalogue (prix posés par le serveur). */
function AiCatalogToggle({ canManage }) {
  const { brand, activeCompany, fetchCompanies } = useAuth();
  const [saving, setSaving] = useState(false);
  const toggle = async (on) => {
    setSaving(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, { ai_catalog_only: on });
      await fetchCompanies();
      toast.success(on ? "IA limitée au catalogue : activée" : "IA limitée au catalogue : désactivée");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-card border border-border rounded-sm p-4 flex items-start gap-3" data-testid="ai-catalog-only">
      <Switch id="ai-catalog-only" checked={!!brand?.ai_catalog_only} onCheckedChange={toggle} disabled={!canManage || saving}
        data-testid="ai-catalog-only-switch" className="mt-0.5" />
      <div>
        <Label htmlFor="ai-catalog-only" className="font-medium">IA limitée au catalogue</Label>
        <p className="text-xs text-muted-foreground mt-0.5">
          Les générations IA (nouvelle proposition, « Générer avec l'IA », modèles) ne proposent que les services de ce catalogue, à ses prix,
          et le conseiller IA ne modifie plus les prix. La saisie manuelle dans l'éditeur reste libre.
        </p>
      </div>
    </div>
  );
}

function ProductsTab({ products, reload, canManage }) {
  const [showArchived, setShowArchived] = useState(false);
  const [translating, setTranslating] = useState(null); // produit dont on édite la version anglaise
  const [dialogProduct, setDialogProduct] = useState(undefined); // undefined = fermé, null = création, objet = édition

  useEffect(() => {
    if (dialogProduct) {
      const fresh = products.find((p) => p.id === dialogProduct.id);
      if (fresh) setDialogProduct(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const close = () => setDialogProduct(undefined);

  const toggleActive = async (p) => {
    try {
      await api.put(`/catalog/products/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Produit archivé" : "Produit réactivé");
      reload();
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  const visible = products.filter((p) => showArchived || p.active);
  const grouped = Object.entries(
    visible.reduce((acc, p) => {
      const k = p.category || "Sans catégorie";
      (acc[k] = acc[k] || []).push(p);
      return acc;
    }, {})
  ).sort(([a], [b]) => a.localeCompare(b, "fr"));

  return (
    <div className="space-y-6">
      <AiCatalogToggle canManage={canManage} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} data-testid="catalog-show-archived" />
          Afficher les produits archivés
        </label>
        {canManage && (
          <Button onClick={() => setDialogProduct(null)} data-testid="new-product-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Nouveau produit
          </Button>
        )}
      </div>

      {grouped.length === 0 && (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center text-muted-foreground" data-testid="empty-products">
          Aucun produit dans le catalogue.
        </div>
      )}

      {grouped.map(([category, items]) => (
        <div key={category} className="space-y-2">
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{category}</h3>
          <div className="space-y-2">
            {items.map((p) => (
              <div key={p.id} className={`flex items-center justify-between gap-3 border border-border rounded-sm px-4 py-3 bg-card ${!p.active ? "opacity-60" : ""}`} data-testid={`catalog-product-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    {p.name}
                    {p.is_telecom && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">Télécom</span>}
                    {p.requires_fibre_number && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">N° de fibre</span>}
                    {p.requires_phone_number && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">Choix du numéro</span>}
                    {!p.visible_to_partners && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-muted text-muted-foreground border border-border">Masqué aux partenaires</span>}
                    {p.visible_to_team === false && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-muted text-muted-foreground border border-border">Masqué à l'équipe</span>}
                    {!p.active && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-muted text-muted-foreground border border-border">Archivé</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {fmtMoney(p.commitment_options?.[0]?.unit_price ?? p.unit_price)} HT {p.recurrence === "mensuel" ? "/mois" : "unique"} · {commitmentSummary(p.commitment_options)}
                    {p.links?.length > 0 && ` · ${p.links.length} produit${p.links.length > 1 ? "s" : ""} lié${p.links.length > 1 ? "s" : ""}`}
                  </p>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <EnButton onClick={() => setTranslating(p)} done={!!p.name_en || !!p.description_en} testId={`translate-product-${p.id}`} />
                    <button onClick={() => setDialogProduct(p)} data-testid={`edit-product-${p.id}`} title="Modifier" className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => toggleActive(p)} data-testid={`archive-product-${p.id}`} title={p.active ? "Archiver" : "Réactiver"} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                      {p.active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {dialogProduct !== undefined && (
        <ProductDialog product={dialogProduct} products={products} onClose={close}
          onSaved={() => { close(); reload(); }} onRefresh={reload} />
      )}
      {translating && (
        <TranslationDialog title={`Version anglaise — ${translating.name}`} onClose={() => setTranslating(null)}
          pairs={[
            { key: "name_en", label: "Nom", fr: translating.name, en: translating.name_en },
            ...(translating.description ? [{ key: "description_en", label: "Description", fr: translating.description, en: translating.description_en }] : []),
            ...(translating.summary_details ? [{ key: "summary_details_en", label: "Récapitulatif contractuel (débits, équipements)", fr: translating.summary_details, en: translating.summary_details_en }] : []),
          ]}
          onSave={async (en) => { await api.put(`/catalog/products/${translating.id}`, en); reload(); }} />
      )}
    </div>
  );
}

/**
 * Prix dynamiques : la 1re durée (par défaut) suit TOUJOURS le prix unitaire, les autres aussi sauf prix propre ; aucun
 * prix n'est recopié. Un prix propre sur la 1re durée (donnée antérieure, ou durée devenue la 1re) est retiré et renvoyé
 * dans `dropped` ({ months, price }) pour être signalé s'il diffère du prix unitaire. `unit_price` : texte, "" = aucun.
 */
function asDefaultFirst(rows, unit) {
  if (!rows.length || rows[0].unit_price === "") return { rows, dropped: null };
  const price = rows[0].unit_price;
  return {
    rows: [{ ...rows[0], unit_price: "" }, ...rows.slice(1)],
    dropped: Number(price) !== Number(unit) ? { months: rows[0].months, price } : null,
  };
}

function ProductDialog({ product, products, onClose, onSaved, onRefresh }) {
  const [initial] = useState(() => asDefaultFirst(
    (product?.commitment_options || []).map((c) => ({ months: String(c.months), unit_price: c.unit_price != null ? String(c.unit_price) : "" })),
    product?.unit_price,
  ));
  const [dropped, setDropped] = useState(initial.dropped);
  const [form, setForm] = useState({
    name: product?.name || "",
    description: product?.description || "",
    category: product?.category || "",
    unit_price: product?.unit_price != null ? String(product.unit_price) : "",
    recurrence: product?.recurrence || "mensuel",
    is_telecom: product?.is_telecom || false,
    summary_details: product?.summary_details || "",
    channels_mode: product?.channels_mode || "",
    channels_choice: product?.channels_choice != null ? String(product.channels_choice) : "",
    provider_id: product?.provider_id || "",
    min_qty: product?.min_qty != null ? String(product.min_qty) : "1",
    max_qty: product?.max_qty != null ? String(product.max_qty) : "",
    visible_to_partners: product?.visible_to_partners !== false,
    visible_to_team: product?.visible_to_team !== false,
    requires_fibre_number: !!product?.requires_fibre_number,
    requires_phone_number: !!product?.requires_phone_number,
    sort_order: product?.sort_order != null ? String(product.sort_order) : "0",
  });
  const [commitments, setCommitments] = useState(initial.rows);
  // 1re durée supprimée : la suivante devient la durée par défaut et suit le prix unitaire (ancien prix signalé).
  const removeCommitment = (i) => {
    const next = commitments.filter((_, xi) => xi !== i);
    if (i > 0) return setCommitments(next);
    const r = asDefaultFirst(next, form.unit_price);
    if (r.dropped) setDropped(r.dropped);
    setCommitments(r.rows);
  };
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  useEffect(() => { api.get("/catalog/providers").then((r) => setProviders(r.data)).catch(() => setProviders([])); }, []);

  const [links, setLinks] = useState((product?.links || []).map((l) => ({ child_id: l.child_id, child_name: l.child_name, required: l.required, quantity_mode: l.quantity_mode, quantity: l.quantity, max_per_parent: l.max_per_parent ?? null, included_per_parent: l.included_per_parent ?? null, unit_label: l.unit_label ?? "" })));
  const [addLinkId, setAddLinkId] = useState("");
  const [savingLinks, setSavingLinks] = useState(false);

  useEffect(() => {
    setLinks((product?.links || []).map((l) => ({ child_id: l.child_id, child_name: l.child_name, required: l.required, quantity_mode: l.quantity_mode, quantity: l.quantity, max_per_parent: l.max_per_parent ?? null, included_per_parent: l.included_per_parent ?? null, unit_label: l.unit_label ?? "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    if (form.unit_price === "" || Number.isNaN(Number(form.unit_price))) return toast.error("Prix HT invalide");
    // Ligne vide en tête : écartée à l'envoi, la suivante deviendrait la durée par défaut à l'insu de l'utilisateur.
    if (commitments.length > 1 && commitments[0].months === "") return toast.error("Indiquez la 1re durée d'engagement, ou supprimez sa ligne.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        category: form.category || undefined,
        unit_price: Number(form.unit_price),
        recurrence: form.recurrence,
        commitment_options: commitments.filter((c) => c.months !== "").map((c) => ({ months: Number(c.months), unit_price: c.unit_price === "" ? null : Number(c.unit_price) })),
        is_telecom: form.is_telecom,
        summary_details: form.summary_details || undefined,
        channels_mode: form.channels_mode || null,
        channels_choice: form.channels_mode === "choice" ? Number(form.channels_choice) || null : null,
        provider_id: form.provider_id || null,
        min_qty: Number(form.min_qty) || 1,
        max_qty: form.max_qty === "" ? null : Number(form.max_qty),
        visible_to_partners: form.visible_to_partners,
        visible_to_team: form.visible_to_team,
        requires_fibre_number: form.requires_fibre_number,
        requires_phone_number: form.requires_phone_number,
        sort_order: Number(form.sort_order) || 0,
      };
      if (product) await api.put(`/catalog/products/${product.id}`, payload);
      else await api.post("/catalog/products", payload);
      toast.success(product ? "Produit mis à jour" : "Produit créé");
      onSaved();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const addLink = () => {
    if (!addLinkId) return;
    const child = products.find((p) => p.id === addLinkId);
    if (!child || links.some((l) => l.child_id === child.id)) return;
    setLinks((ls) => [...ls, { child_id: child.id, child_name: child.name, required: true, quantity_mode: "fixed", quantity: 1 }]);
    setAddLinkId("");
  };
  const patchLink = (childId, patch) => setLinks((ls) => ls.map((l) => {
    if (l.child_id !== childId) return l;
    const next = { ...l, ...patch };
    return next.required || next.quantity_mode !== "fixed" ? { ...next, max_per_parent: null, included_per_parent: null, unit_label: "" } : next;
  }));
  const removeLink = (childId) => setLinks((ls) => ls.filter((l) => l.child_id !== childId));

  const saveLinks = async () => {
    setSavingLinks(true);
    try {
      await api.put(`/catalog/products/${product.id}/links`, {
        links: links.map((l) => ({
          child_id: l.child_id, required: l.required, quantity_mode: l.quantity_mode, quantity: Number(l.quantity) || 1,
          max_per_parent: l.max_per_parent === "" || l.max_per_parent == null ? null : Number(l.max_per_parent),
          // Saisie par nombre total (ex. TV) : inclus par unité + libellé, avec le max ci-dessus.
          included_per_parent: l.included_per_parent === "" || l.included_per_parent == null ? null : Number(l.included_per_parent),
          unit_label: (l.unit_label || "").trim() || null,
        })),
      });
      toast.success("Produits liés enregistrés");
      onRefresh();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingLinks(false); }
  };

  const linkCandidates = products.filter((p) => p.id !== product?.id && !links.some((l) => l.child_id === p.id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{product ? "Modifier le produit" : "Nouveau produit"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="product-name" className="mt-1.5 rounded-sm" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} data-testid="product-description" className="mt-1.5 rounded-sm" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Ex : Internet" data-testid="product-category" className="mt-1.5 rounded-sm" /></div>
            <div>
              <Label>Récurrence</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm((f) => ({ ...f, recurrence: v }))}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="product-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="unique">Unique</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label>Prix unitaire HT *</Label><Input type="number" min={0} step="0.01" value={form.unit_price} onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} data-testid="product-unit-price" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Quantité min</Label><Input type="number" min={1} value={form.min_qty} onChange={(e) => setForm((f) => ({ ...f, min_qty: e.target.value }))} data-testid="product-min-qty" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Quantité max</Label><Input type="number" min={1} value={form.max_qty} onChange={(e) => setForm((f) => ({ ...f, max_qty: e.target.value }))} placeholder="Illimitée" data-testid="product-max-qty" className="mt-1.5 rounded-sm" /></div>
          </div>

          <div>
            <Label>Durées d'engagement</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">1ʳᵉ = durée par défaut, au prix unitaire HT ; les suivantes peuvent avoir leur propre prix. 0 mois ou aucune durée = sans engagement.</p>
            {dropped && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-2" data-testid="default-price-notice">
                La durée « {dropped.months === "0" ? "sans engagement" : `${dropped.months} mois`} » avait son propre prix ({fmtMoney(Number(dropped.price))}) :
                durée par défaut, elle suit désormais le prix unitaire ({fmtMoney(Number(form.unit_price) || 0)}). Vérifiez le prix unitaire avant d'enregistrer.
              </p>
            )}
            <div className="space-y-2">
              {commitments.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input type="number" min={0} value={c.months} onChange={(e) => setCommitments((cs) => cs.map((x, xi) => (xi === i ? { ...x, months: e.target.value } : x)))} placeholder="Mois" data-testid={`commitment-months-${i}`} className="rounded-sm w-24" />
                  <span className="text-xs text-muted-foreground shrink-0">mois</span>
                  {i === 0
                    ? <span className="text-xs text-muted-foreground flex-1" data-testid="commitment-price-0">au prix unitaire HT (durée par défaut)</span>
                    : <Input type="number" min={0} step="0.01" value={c.unit_price} onChange={(e) => setCommitments((cs) => cs.map((x, xi) => (xi === i ? { ...x, unit_price: e.target.value } : x)))} placeholder="Prix HT (optionnel)" data-testid={`commitment-price-${i}`} className="rounded-sm flex-1" />}
                  <button type="button" onClick={() => removeCommitment(i)} data-testid={`remove-commitment-${i}`} className="text-muted-foreground hover:text-destructive shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setCommitments((cs) => [...cs, { months: "", unit_price: "" }])} data-testid="add-commitment" className="rounded-sm">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une durée
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Service télécom</p>
              <p className="text-xs text-muted-foreground">Internet, téléphonie… déclenche le récapitulatif contractuel.</p>
            </div>
            <Switch checked={form.is_telecom} onCheckedChange={(v) => setForm((f) => ({ ...f, is_telecom: v }))} data-testid="product-is-telecom" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Numéro de fibre exigé</p>
              <p className="text-xs text-muted-foreground">Le partenaire le saisit à la création, ou le client avant de signer (« à fournir par le client »).</p>
            </div>
            <Switch checked={form.requires_fibre_number} onCheckedChange={(v) => setForm((f) => ({ ...f, requires_fibre_number: v }))} data-testid="product-requires-fibre-number" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Ligne téléphonique : choix du numéro</p>
              <p className="text-xs text-muted-foreground">Le partenaire choisit à la création : garder le numéro du client (portabilité, RIO facultatif au départ) ou lui attribuer un nouveau numéro.</p>
            </div>
            <Switch checked={form.requires_phone_number} onCheckedChange={(v) => setForm((f) => ({ ...f, requires_phone_number: v }))} data-testid="product-requires-phone-number" />
          </div>
          {form.is_telecom && (
            <div>
              <Label>Détails pour le récapitulatif contractuel</Label>
              <Textarea value={form.summary_details} onChange={(e) => setForm((f) => ({ ...f, summary_details: e.target.value }))} rows={2}
                placeholder="Débits, équipements…" data-testid="product-summary-details" className="mt-1.5 rounded-sm" />
            </div>
          )}

          <div>
            <Label htmlFor="product-channels-mode">Chaînes TV</Label>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <select id="product-channels-mode" value={form.channels_mode} data-testid="product-channels-mode"
                onChange={(e) => setForm((f) => ({ ...f, channels_mode: e.target.value }))}
                className="h-9 border border-border rounded-sm px-2 text-sm bg-background">
                <option value="">Aucune (pas une offre TV)</option>
                <option value="core">Cœur seul (rien à choisir)</option>
                <option value="choice">Cœur + chaînes au choix après signature</option>
                <option value="all">Toutes les chaînes incluses</option>
              </select>
              {form.channels_mode === "choice" && (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Nombre de chaînes au choix
                  <Input type="number" min={1} value={form.channels_choice} data-testid="product-channels-choice"
                    onChange={(e) => setForm((f) => ({ ...f, channels_choice: e.target.value }))} className="w-20 h-9 rounded-sm" />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Les chaînes (onglet « Chaînes TV ») s'affichent avec leurs logos dans l'offre ; le cœur est inclus dans toutes les formules ; « au choix » : le client choisit parmi les chaînes « au choix » après la signature.</p>
          </div>

          <div>
            <Label htmlFor="product-provider">Prestataire</Label>
            <select id="product-provider" value={form.provider_id} data-testid="product-provider"
              onChange={(e) => setForm((f) => ({ ...f, provider_id: e.target.value }))}
              className="mt-1.5 h-9 w-full border border-border rounded-sm px-2 text-sm bg-background">
              <option value="">Aucun (service assuré par vous)</option>
              {providers.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">Le client voit « Service assuré par … » et doit accepter les documents du prestataire (onglet « Prestataires ») pour signer.</p>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Visible des partenaires</p>
              <p className="text-xs text-muted-foreground">Désactivez pour masquer ce produit du catalogue partenaire.</p>
            </div>
            <Switch checked={form.visible_to_partners} onCheckedChange={(v) => setForm((f) => ({ ...f, visible_to_partners: v }))} data-testid="product-visible-to-partners" />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Proposé à l'équipe</p>
              <p className="text-xs text-muted-foreground">Désactivez pour retirer ce produit de l'IA « limitée au catalogue » et des sélections de l'équipe, sans l'archiver (les offres déjà émises le gardent).</p>
            </div>
            <Switch checked={form.visible_to_team} onCheckedChange={(v) => setForm((f) => ({ ...f, visible_to_team: v }))} data-testid="product-visible-to-team" />
          </div>

          <div><Label>Ordre d'affichage</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} data-testid="product-sort-order" className="mt-1.5 rounded-sm w-28" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-product" className="rounded-sm bg-primary hover:bg-primary/90">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>

        {product && (
          <div className="border-t border-border pt-4 space-y-3">
            <h4 className="font-heading text-sm font-semibold">Produits liés</h4>
            <div className="space-y-2">
              {links.map((l) => (
                <div key={l.child_id} className="flex flex-wrap items-center gap-2 text-sm border border-border rounded-sm px-3 py-2" data-testid={`link-row-${l.child_id}`}>
                  <span className="flex-1 min-w-[8rem] truncate">{l.child_name || products.find((p) => p.id === l.child_id)?.name || l.child_id}</span>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                    <Checkbox checked={l.required} onCheckedChange={(v) => patchLink(l.child_id, { required: !!v })} data-testid={`link-required-${l.child_id}`} />
                    Obligatoire
                  </label>
                  <Select value={l.quantity_mode} onValueChange={(v) => patchLink(l.child_id, { quantity_mode: v })}>
                    <SelectTrigger className="h-8 w-40 rounded-sm text-xs shrink-0" data-testid={`link-mode-${l.child_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Par offre</SelectItem>
                      <SelectItem value="per_parent">Par unité du service</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" min={1} value={l.quantity} onChange={(e) => patchLink(l.child_id, { quantity: e.target.value })} data-testid={`link-quantity-${l.child_id}`} className="w-16 h-8 rounded-sm text-xs shrink-0" />
                  {!l.required && l.quantity_mode === "fixed" && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0" title="Quantité maximale de cette option par unité du service (ex. 4 écrans TV par formule). Vide = sans plafond.">
                      Max. par unité
                      <Input type="number" min={1} value={l.max_per_parent ?? ""} placeholder="—"
                        onChange={(e) => patchLink(l.child_id, { max_per_parent: e.target.value })}
                        data-testid={`link-max-per-parent-${l.child_id}`} className="w-14 h-8 rounded-sm text-xs" />
                    </label>
                  )}
                  {!l.required && l.quantity_mode === "fixed" && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0" title="Saisie par nombre total : unités déjà comprises dans chaque unité du service (ex. 1 écran par formule TV) et nom de l'unité (ex. TV). Le partenaire saisit alors « Nombre de TV ».">
                      Inclus par unité
                      <Input type="number" min={1} value={l.included_per_parent ?? ""} placeholder="—"
                        onChange={(e) => patchLink(l.child_id, { included_per_parent: e.target.value })}
                        data-testid={`link-included-per-parent-${l.child_id}`} className="w-14 h-8 rounded-sm text-xs" />
                      <Input value={l.unit_label ?? ""} placeholder="Unité (ex. TV)"
                        onChange={(e) => patchLink(l.child_id, { unit_label: e.target.value })}
                        data-testid={`link-unit-label-${l.child_id}`} className="w-24 h-8 rounded-sm text-xs" />
                    </label>
                  )}
                  <button onClick={() => removeLink(l.child_id)} data-testid={`remove-link-${l.child_id}`} className="text-muted-foreground hover:text-destructive shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Select value={addLinkId} onValueChange={setAddLinkId}>
                <SelectTrigger className="rounded-sm flex-1" data-testid="add-link-select"><SelectValue placeholder="Ajouter un produit lié…" /></SelectTrigger>
                <SelectContent>
                  {linkCandidates.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={addLink} disabled={!addLinkId} data-testid="add-link-button" className="rounded-sm shrink-0">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={saveLinks} disabled={savingLinks} data-testid="save-links" className="rounded-sm">
              {savingLinks ? "Enregistrement…" : "Enregistrer les liens"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Partenaires ───────────────────────────

function PartnersTab({ products, canManage }) {
  const [partners, setPartners] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [dialogPartner, setDialogPartner] = useState(undefined);

  const load = () =>
    api.get("/partners").then((r) => setPartners(r.data))
      .catch((e) => {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error(formatAxiosError(e));
      });

  useEffect(() => { load(); }, []);

  // Resynchronise le partenaire ouvert dans la modale après une mutation qui ne la ferme
  // pas (remises, membres) — la modale garde un instantané, jamais la référence live.
  useEffect(() => {
    if (dialogPartner && partners) {
      const fresh = partners.find((p) => p.id === dialogPartner.id);
      if (fresh) setDialogPartner(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners]);

  if (forbidden) return <p className="text-sm text-muted-foreground" data-testid="partners-forbidden">Accès réservé aux administrateurs de la société.</p>;
  if (partners === null) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  const close = () => setDialogPartner(undefined);

  return (
    <div className="space-y-6">
      {canManage && (
        <div className="flex justify-end">
          <Button onClick={() => setDialogPartner(null)} data-testid="new-partner-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Nouveau partenaire
          </Button>
        </div>
      )}

      {partners.length === 0 ? (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center text-muted-foreground" data-testid="empty-partners">
          Aucun partenaire pour l'instant.
        </div>
      ) : (
        <div className="space-y-2">
          {partners.map((p) => (
            <div key={p.id} className={`flex items-center justify-between gap-3 border border-border rounded-sm px-4 py-3 bg-card ${!p.active ? "opacity-60" : ""}`} data-testid={`partner-row-${p.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-2">
                  {p.name}
                  {!p.active && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-muted text-muted-foreground border border-border">Inactif</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {p.contact_email || "Sans email de contact"} · Remise {p.discount_percent || 0} % · {p.discount_mode === "margin" ? "Marge partenaire" : "Remise affichée au client"} · {p.members.length} membre{p.members.length > 1 ? "s" : ""}
                </p>
              </div>
              {canManage && (
                <button onClick={() => setDialogPartner(p)} data-testid={`edit-partner-${p.id}`} title="Modifier" className="p-1.5 text-muted-foreground hover:text-primary transition-colors shrink-0"><Pencil className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {dialogPartner !== undefined && (
        <PartnerDialog partner={dialogPartner} products={products} onClose={close}
          onSaved={() => { close(); load(); }} onRefresh={load} />
      )}
    </div>
  );
}

function PartnerDialog({ partner, products, onClose, onSaved, onRefresh }) {
  const [form, setForm] = useState({
    name: partner?.name || "",
    contact_email: partner?.contact_email || "",
    discount_percent: partner?.discount_percent != null ? String(partner.discount_percent) : "0",
    discount_mode: partner?.discount_mode || "client",
    client_vat_rate: partner?.client_vat_rate != null ? String(partner.client_vat_rate) : "",
    active: partner?.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const [discountMap, setDiscountMap] = useState(() => Object.fromEntries((partner?.product_discounts || []).map((d) => [d.product_id, String(d.discount_percent)])));
  const [savingDiscounts, setSavingDiscounts] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    setDiscountMap(Object.fromEntries((partner?.product_discounts || []).map((d) => [d.product_id, String(d.discount_percent)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_email: form.contact_email.trim() || null,
        discount_percent: Number(form.discount_percent) || 0,
        discount_mode: form.discount_mode,
        client_vat_rate: form.client_vat_rate === "" ? null : Number(form.client_vat_rate),
      };
      if (partner) {
        payload.active = form.active;
        await api.put(`/partners/${partner.id}`, payload);
        toast.success("Partenaire mis à jour");
      } else {
        await api.post("/partners", payload);
        toast.success("Partenaire créé");
      }
      onSaved();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const saveDiscounts = async () => {
    setSavingDiscounts(true);
    try {
      const discounts = Object.entries(discountMap)
        .filter(([, v]) => v !== "" && v != null)
        .map(([product_id, v]) => ({ product_id, discount_percent: Number(v) }));
      await api.put(`/partners/${partner.id}/discounts`, { discounts });
      toast.success("Remises enregistrées");
      onRefresh();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingDiscounts(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Email requis");
    setInviting(true);
    try {
      const { data } = await api.post(`/partners/${partner.id}/members`, { email: inviteEmail.trim(), ...(inviteName.trim() && { name: inviteName.trim() }) });
      toast.success(data.is_new ? `Compte créé et invité (${data.email})` : `${data.email} ajouté`);
      setInviteEmail(""); setInviteName("");
      onRefresh();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setInviting(false); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Retirer ce membre du partenaire ?")) return;
    try {
      await api.delete(`/partners/${partner.id}/members/${userId}`);
      toast.success("Membre retiré");
      onRefresh();
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{partner ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="partner-name" className="mt-1.5 rounded-sm" /></div>
            <div><Label>Email de contact</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} data-testid="partner-email" className="mt-1.5 rounded-sm" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Remise globale (%)</Label>
              <Input type="number" min={0} max={100} step="0.1" value={form.discount_percent} onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))} data-testid="partner-discount-percent" className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label>TVA des clients du partenaire (%)</Label>
              <Input type="number" min={0} max={100} step="0.1" value={form.client_vat_rate} onChange={(e) => setForm((f) => ({ ...f, client_vat_rate: e.target.value }))}
                placeholder="Taux de la société" data-testid="partner-vat-rate" className="mt-1.5 rounded-sm" />
              <p className="text-xs text-muted-foreground mt-1.5">Vide = taux de la société. Ex. 0 pour Saint-Barthélemy.</p>
            </div>
          </div>
          <div>
            <Label>Mode de remise</Label>
            <RadioGroup value={form.discount_mode} onValueChange={(v) => setForm((f) => ({ ...f, discount_mode: v }))} className="mt-2" data-testid="partner-discount-mode">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="client" id="partner-mode-client" className="mt-0.5" />
                <span>Remise affichée au client <span className="text-muted-foreground">— le client voit « −{form.discount_percent || 0} % »</span></span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="margin" id="partner-mode-margin" className="mt-0.5" />
                <span>Marge du partenaire <span className="text-muted-foreground">— le client paie le prix catalogue, la remise reste une marge visible par vous et le partenaire</span></span>
              </label>
            </RadioGroup>
          </div>
          {partner && (
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium">Partenaire actif</p>
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="partner-active" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-partner" className="rounded-sm bg-primary hover:bg-primary/90">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>

        {partner && (
          <>
            <div className="border-t border-border pt-4 space-y-3">
              <h4 className="font-heading text-sm font-semibold">Remises par produit</h4>
              <p className="text-xs text-muted-foreground">Vide = remise globale ci-dessus.</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {products.filter((p) => p.active).map((p) => (
                  <div key={p.id} className="flex items-center gap-3 text-sm">
                    <span className="flex-1 truncate">{p.name}</span>
                    <Input type="number" min={0} max={100} step="0.1" value={discountMap[p.id] ?? ""}
                      onChange={(e) => setDiscountMap((m) => ({ ...m, [p.id]: e.target.value }))}
                      placeholder="—" data-testid={`partner-product-discount-${p.id}`} className="w-20 h-8 rounded-sm text-xs" />
                    <span className="text-xs text-muted-foreground w-3">%</span>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={saveDiscounts} disabled={savingDiscounts} data-testid="save-partner-discounts" className="rounded-sm">
                {savingDiscounts ? "Enregistrement…" : "Enregistrer les remises"}
              </Button>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <h4 className="font-heading text-sm font-semibold">Membres</h4>
              <div className="space-y-1.5">
                {partner.members.length === 0 && <p className="text-xs text-muted-foreground">Aucun membre pour l'instant.</p>}
                {partner.members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 text-sm py-1" data-testid={`partner-member-${m.id}`}>
                    <span className="truncate">{m.name || m.email}{m.name && <span className="text-xs text-muted-foreground"> ({m.email})</span>}</span>
                    <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`} className="text-xs text-muted-foreground hover:text-destructive shrink-0">Retirer</button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="email@partenaire.com" data-testid="invite-member-email" className="rounded-sm flex-1" />
                <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Nom (optionnel)" data-testid="invite-member-name" className="rounded-sm sm:w-48" />
                <Button onClick={invite} disabled={inviting} data-testid="invite-member-submit" className="rounded-sm shrink-0">
                  <UserPlus className="w-4 h-4 mr-1" /> Inviter
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Modèles partenaires ───────────────────────────

function PartnerTemplateCard({ tpl, products, canManage, onChanged }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(!!tpl.partner_visible);
  const [translating, setTranslating] = useState(false);
  const translatable = tpl.translatable || [];
  const translated = translatable.filter((t) => tpl.translations_en?.[t.path]).length;
  const [selection, setSelection] = useState(() =>
    Object.fromEntries((tpl.catalog_preset?.products || []).map((p) => [p.product_id, { preselected: !!p.preselected, quantity: p.quantity ?? 1 }]))
  );
  const [saving, setSaving] = useState(false);

  const sellable = products.filter((p) => p.active && p.visible_to_partners);

  const toggleProduct = (id) => setSelection((s) => {
    if (s[id]) {
      const next = { ...s };
      delete next[id];
      return next;
    }
    return { ...s, [id]: { preselected: false, quantity: 1 } };
  });
  const patchProduct = (id, patch) => setSelection((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/templates/${tpl.id}`, {
        partner_visible: visible,
        catalog_preset: { products: Object.entries(selection).map(([product_id, v]) => ({ product_id, preselected: !!v.preselected, quantity: Number(v.quantity) || 1 })) },
      });
      toast.success("Modèle partenaire enregistré");
      onChanged();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-sm p-4 lg:p-6 space-y-4" data-testid={`partner-template-${tpl.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{tpl.name}</p>
          {tpl.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{tpl.description}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {canManage && (
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-sm" data-testid={`edit-partner-template-${tpl.id}`}
              onClick={() => navigate(`/templates/${tpl.id}/edit`, { state: { from: "/catalog" } })}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier le contenu
            </Button>
          )}
          <label className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Proposé aux partenaires</span>
            <Switch checked={visible} disabled={!canManage} onCheckedChange={setVisible} data-testid={`partner-template-visible-${tpl.id}`} />
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground" data-testid={`partner-template-en-${tpl.id}`}>
        <span>Version anglaise : {translated} texte{translated > 1 ? "s" : ""} traduit{translated > 1 ? "s" : ""} sur {translatable.length}{tpl.name_en ? " · nom traduit" : ""}</span>
        {canManage && (
          <Button type="button" size="sm" variant="outline" onClick={() => setTranslating(true)} className="h-8 rounded-sm" data-testid={`translate-template-${tpl.id}`}>
            <Languages className="w-3.5 h-3.5 mr-1" /> Anglais
          </Button>
        )}
      </div>
      {translating && (
        <TranslationDialog title={`Version anglaise — ${tpl.name}`} onClose={() => setTranslating(false)}
          pairs={[
            { key: "__name", label: "Nom de la proposition", fr: tpl.name, en: tpl.name_en },
            ...translatable.map((t) => ({ key: t.path, fr: t.fr, en: tpl.translations_en?.[t.path] })),
          ]}
          onSave={async ({ __name, ...translations }) => {
            await api.put(`/templates/${tpl.id}`, { name_en: __name || null, translations_en: translations });
            onChanged();
          }} />
      )}

      {visible && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground pb-1">Services proposables — vide = tout le catalogue.</p>
          {sellable.length === 0 && <p className="text-xs text-muted-foreground">Aucun produit visible des partenaires dans le catalogue.</p>}
          {sellable.map((p) => {
            const sel = selection[p.id];
            return (
              <div key={p.id} className="flex flex-wrap items-center gap-3 text-sm py-1">
                <Checkbox checked={!!sel} disabled={!canManage} onCheckedChange={() => toggleProduct(p.id)} data-testid={`preset-check-${tpl.id}-${p.id}`} />
                <span className="flex-1 min-w-[8rem] truncate">{p.name}</span>
                {sel && (
                  <>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                      <Checkbox checked={sel.preselected} disabled={!canManage} onCheckedChange={(v) => patchProduct(p.id, { preselected: !!v })} data-testid={`preset-preselect-${tpl.id}-${p.id}`} />
                      Pré-coché
                    </label>
                    <Input type="number" min={1} value={sel.quantity} disabled={!canManage}
                      onChange={(e) => patchProduct(p.id, { quantity: e.target.value })}
                      data-testid={`preset-quantity-${tpl.id}-${p.id}`} className="w-16 h-8 rounded-sm text-xs shrink-0" />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canManage && (
        <Button size="sm" onClick={save} disabled={saving} data-testid={`save-partner-template-${tpl.id}`} className="rounded-sm">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      )}
    </div>
  );
}

function PartnerTemplatesTab({ products, canManage }) {
  const [templates, setTemplates] = useState(null);

  const load = () =>
    api.get("/templates").then((r) => setTemplates(r.data.filter((t) => t.custom)))
      .catch((e) => { toast.error(formatAxiosError(e)); setTemplates([]); });

  useEffect(() => { load(); }, []);

  if (templates === null) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Modèles personnalisés proposables aux partenaires. Créez ou modifiez un modèle depuis l'éditeur de
        proposition (« Enregistrer comme modèle »), puis réglez ici les services qu'il propose.
      </p>
      {templates.length === 0 ? (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center text-muted-foreground" data-testid="empty-partner-templates">
          Aucun modèle personnalisé. Créez-en un depuis l'éditeur de proposition.
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((t) => <PartnerTemplateCard key={t.id} tpl={t} products={products} canManage={canManage} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Dossier client ───────────────────────────

function DossierTab({ canManage }) {
  const { brand, activeCompany, fetchCompanies } = useAuth();
  const [docs, setDocs] = useState(brand.required_documents || DEFAULT_REQUIRED_DOCUMENTS);
  const [sepaRequired, setSepaRequired] = useState(!!brand.require_sepa_mandate);
  const [sepaDelay, setSepaDelay] = useState(!!brand.sepa_mandate_delay);
  const [summary, setSummary] = useState({
    legal_identity: brand.contract_summary?.legal_identity || "",
    duration_terms: brand.contract_summary?.duration_terms || "",
    accessibility: brand.contract_summary?.accessibility || "",
    other_info: brand.contract_summary?.other_info || "",
  });
  const [saving, setSaving] = useState(false);
  const [translatingSummary, setTranslatingSummary] = useState(false);
  // Récapitulatif anglais (clients anglophones) : textes saisis ici, repli sur le français.
  const SUMMARY_EN = [["duration_terms", "Durée, reconduction, résiliation"], ["accessibility", "Accessibilité"], ["other_info", "Autres informations"]];

  useEffect(() => {
    setDocs(brand.required_documents || DEFAULT_REQUIRED_DOCUMENTS);
    setSepaRequired(!!brand.require_sepa_mandate);
    setSepaDelay(!!brand.sepa_mandate_delay);
    setSummary({
      legal_identity: brand.contract_summary?.legal_identity || "",
      duration_terms: brand.contract_summary?.duration_terms || "",
      accessibility: brand.contract_summary?.accessibility || "",
      other_info: brand.contract_summary?.other_info || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany]);

  const toggleDoc = (clientType, docType) => setDocs((d) => {
    const list = d[clientType] || [];
    return { ...d, [clientType]: list.includes(docType) ? list.filter((t) => t !== docType) : [...list, docType] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${activeCompany}/settings`, { required_documents: docs, require_sepa_mandate: sepaRequired, sepa_mandate_delay: sepaDelay, contract_summary: { ...brand.contract_summary, ...summary } }); // garde les textes anglais
      await fetchCompanies();
      toast.success("Dossier client enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-6">
        <div>
          <h3 className="font-heading text-lg font-semibold">Pièces requises</h3>
          <p className="text-sm text-muted-foreground mt-1">Pièces à fournir par le client selon son type, avant que le dossier soit considéré complet.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-semibold">Pièce</th>
                {CLIENT_TYPES.map((ct) => <th key={ct.value} className="py-2 px-3 font-semibold text-center whitespace-nowrap">{ct.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(DOCUMENT_TYPES).map(([type, label]) => (
                <tr key={type} className="border-t border-border">
                  <td className="py-2.5 pr-4">{label}</td>
                  {CLIENT_TYPES.map((ct) => (
                    <td key={ct.value} className="py-2.5 px-3 text-center">
                      <Checkbox checked={(docs[ct.value] || []).includes(type)} disabled={!canManage}
                        onCheckedChange={() => toggleDoc(ct.value, type)} data-testid={`doc-${ct.value}-${type}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
          <div>
            <p className="font-medium text-sm">Mandat SEPA obligatoire après signature</p>
            <p className="text-xs text-muted-foreground">Le dossier client ne sera complet qu'une fois le mandat de prélèvement signé.</p>
          </div>
          <Switch checked={sepaRequired} disabled={!canManage} onCheckedChange={setSepaRequired} data-testid="require-sepa-mandate" />
        </div>
        {sepaRequired && (
          <div className="flex items-center justify-between gap-4 pl-4 border-l-2 border-border">
            <div>
              <p className="font-medium text-sm">Délai de 7 jours avant le mandat (ventes conclues chez le client)</p>
              <p className="text-xs text-muted-foreground">
                Désactivé (par défaut, ventes à distance) : le client signe son mandat juste après la proposition. Activé : pour un
                particulier ou une TPE, le mandat n'est proposé qu'après 7 jours — Code de la consommation, art. L221-10 (démarchage).
              </p>
            </div>
            <Switch checked={sepaDelay} disabled={!canManage} onCheckedChange={setSepaDelay} data-testid="sepa-mandate-delay" />
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold">Récapitulatif contractuel</h3>
            <p className="text-sm text-muted-foreground mt-1">Textes utilisés pour générer le récapitulatif contractuel des propositions incluant un service télécom.</p>
          </div>
          {canManage && (
            <Button type="button" variant="outline" size="sm" onClick={() => setTranslatingSummary(true)} className="rounded-sm" data-testid="translate-contract-summary">
              <Languages className="w-3.5 h-3.5 mr-1" /> Anglais
            </Button>
          )}
        </div>
        {translatingSummary && (
          <TranslationDialog title="Récapitulatif contractuel en anglais" onClose={() => setTranslatingSummary(false)}
            pairs={SUMMARY_EN.filter(([k]) => summary[k]?.trim()).map(([k, label]) => ({ key: `${k}_en`, label, fr: summary[k], en: brand.contract_summary?.[`${k}_en`] }))}
            onSave={async (en) => {
              await api.put(`/companies/${activeCompany}/settings`, { contract_summary: { ...brand.contract_summary, ...summary, ...en } });
              await fetchCompanies();
            }} />
        )}
        <div><Label>Identité légale (raison sociale, adresse, contact)</Label>
          <Textarea value={summary.legal_identity} disabled={!canManage} onChange={(e) => setSummary((s) => ({ ...s, legal_identity: e.target.value }))} rows={2} data-testid="contract-summary-legal-identity" className="mt-1.5 rounded-sm" /></div>
        <div><Label>Durée, reconduction, résiliation</Label>
          <Textarea value={summary.duration_terms} disabled={!canManage} onChange={(e) => setSummary((s) => ({ ...s, duration_terms: e.target.value }))} rows={2} data-testid="contract-summary-duration" className="mt-1.5 rounded-sm" /></div>
        <div><Label>Accessibilité</Label>
          <Textarea value={summary.accessibility} disabled={!canManage} onChange={(e) => setSummary((s) => ({ ...s, accessibility: e.target.value }))} rows={2} data-testid="contract-summary-accessibility" className="mt-1.5 rounded-sm" /></div>
        <div><Label>Autres informations</Label>
          <Textarea value={summary.other_info} disabled={!canManage} onChange={(e) => setSummary((s) => ({ ...s, other_info: e.target.value }))} rows={2} data-testid="contract-summary-other" className="mt-1.5 rounded-sm" /></div>
        {canManage && (
          <div className="pt-1">
            <Button onClick={save} disabled={saving} data-testid="save-dossier-settings" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Niveau NEXT d'une chaîne : cœur (incluse dans toutes les formules), au choix (Start +10 / +20), All NEXT uniquement. */
const TIER_LABELS = { core: "Cœur (incluse partout)", choice: "Au choix", all: "All NEXT uniquement" };

/** Onglet « Chaînes TV » : line-up affiché avec logos dans les offres TV (import initial : brochure + configurateur NEXT). */
// ─────────────────────────── Prestataires tiers ───────────────────────────

/** Prestataires qui assurent un service du catalogue (ex. NEXTforTV pour la TV) : mention + documents PDF à accepter. */
function ProvidersTab({ canManage }) {
  const [providers, setProviders] = useState(null);
  const [translating, setTranslating] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined = fermé, null = ajout, objet = modification
  const load = () => api.get("/catalog/providers").then((r) => setProviders(r.data)).catch((e) => toast.error(formatAxiosError(e)));
  useEffect(() => { load(); }, []);
  if (!providers) return <p className="text-muted-foreground">Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="providers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          Un produit lié à un prestataire affiche « Service assuré par … » dans l'offre ; ses documents (CGU, CGV…), en PDF ou
          par lien, doivent être acceptés pour signer. Les PDF sont joints à l'exemplaire signé ; un lien, lui, peut changer
          après la signature (le PDF reste la meilleure preuve).
        </p>
        {canManage && (
          <Button onClick={() => setEditing(null)} data-testid="new-provider-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Ajouter un prestataire
          </Button>
        )}
      </div>
      {providers.length === 0 && <p className="text-sm text-muted-foreground">Aucun prestataire.</p>}
      {providers.map((pr) => (
        <div key={pr.id} className="border border-border rounded-sm p-4 bg-card flex items-start justify-between gap-3" data-testid={`provider-row-${pr.id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-medium">{pr.name}</p>
            {pr.note && <p className="text-sm text-muted-foreground mt-0.5">{pr.note}</p>}
            <div className="mt-3 space-y-2">
              {PROVIDER_DOC_SLOTS.map((label) => (
                <ProviderDocSlot key={label} provider={pr} label={label} canManage={canManage} onChanged={load} />
              ))}
              {pr.documents.filter((d) => !isSlotLabel(d.label)).map((d) => (
                <a key={d.file_id || d.url} href={providerDocHref(d) || undefined} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-2 py-1 mr-2 hover:border-primary">
                  {d.url ? <ExternalLink className="w-3.5 h-3.5 text-primary" /> : <FileText className="w-3.5 h-3.5 text-primary" />} {d.label}
                </a>
              ))}
            </div>
          </div>
          {canManage && (
            <div className="flex items-center gap-1 shrink-0">
              {pr.note && <EnButton onClick={() => setTranslating(pr)} done={!!pr.note_en} testId={`translate-provider-${pr.id}`} />}
              <button onClick={() => setEditing(pr)} title="Modifier" data-testid={`edit-provider-${pr.id}`} className="p-1.5 text-muted-foreground hover:text-primary">
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ))}
      {translating && (
        <TranslationDialog title={`Version anglaise — ${translating.name}`} onClose={() => setTranslating(null)}
          pairs={[{ key: "note_en", label: "Mention", fr: translating.note, en: translating.note_en }]}
          onSave={async (en) => { await api.put(`/catalog/providers/${translating.id}`, en); load(); }} />
      )}
      {editing !== undefined && (
        <ProviderDialog provider={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

/** Documents usuels d'un prestataire : un emplacement chacun (téléverser, remplacer, retirer) sur sa fiche. */
const PROVIDER_DOC_SLOTS = ["CGU", "CGV"];
const isSlotLabel = (label) => PROVIDER_DOC_SLOTS.some((l) => l.toLowerCase() === String(label || "").trim().toLowerCase());
/** Document tel que renvoyé au serveur : PDF (file_id) OU lien (url) — l'empreinte est toujours recalculée par le serveur. */
const docPayload = (d) => (d.url ? { label: d.label, url: d.url } : { label: d.label, file_id: d.file_id });

function ProviderDocSlot({ provider, label, canManage, onChanged }) {
  const doc = provider.documents.find((d) => d.label.trim().toLowerCase() === label.toLowerCase());
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null); // null = saisie fermée ; sinon l'adresse en cours de saisie
  const others = provider.documents.filter((d) => d !== doc).map(docPayload);
  const saveDocs = async (documents, message) => {
    await api.put(`/catalog/providers/${provider.id}`, { documents });
    toast.success(message);
    onChanged();
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveDocs([...others, { label, file_id: data.id }], `${label} ${provider.name} ${doc ? "remplacées" : "ajoutées"}`);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(false); }
  };
  // Lien vers le document en ligne (https) : remplace le PDF de l'emplacement ; validé par le serveur.
  const saveLink = async () => {
    setBusy(true);
    try {
      await saveDocs([...others, { label, url: link.trim() }], `${label} ${provider.name} : lien enregistré`);
      setLink(null);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Retirer les ${label} de ${provider.name} ? Elles ne seront plus présentées aux nouveaux signataires.`)) return;
    setBusy(true);
    try { await saveDocs(others, `${label} ${provider.name} retirées`); }
    catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={`provider-slot-${provider.id}-${label}`}>
      <span className="w-10 font-medium">{label}</span>
      {doc ? (
        <a href={providerDocHref(doc) || undefined} target="_blank" rel="noopener noreferrer" data-testid={`provider-slot-open-${provider.id}-${label}`}
          title={doc.url || undefined}
          className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-2 py-1 hover:border-primary max-w-[240px]">
          {doc.url
            ? <><ExternalLink className="w-3.5 h-3.5 text-primary shrink-0" /> <span className="truncate">Lien : {doc.url.replace(/^https:\/\//i, "")}</span></>
            : <><FileText className="w-3.5 h-3.5 text-primary" /> Voir le PDF</>}
        </a>
      ) : (
        <span className="text-xs text-amber-700" data-testid={`provider-slot-missing-${provider.id}-${label}`}>Non fourni</span>
      )}
      {canManage && (
        <>
          <label className={`inline-flex items-center gap-1 text-xs cursor-pointer ${busy ? "text-muted-foreground" : "text-primary"}`}>
            <Upload className="w-3.5 h-3.5" /> {busy ? "Envoi…" : doc?.file_id ? "Remplacer le PDF" : "Téléverser le PDF"}
            <input type="file" accept="application/pdf,.pdf" className="sr-only" disabled={busy} data-testid={`provider-slot-input-${provider.id}-${label}`}
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {link === null && (
            <button type="button" onClick={() => setLink(doc?.url || "")} disabled={busy} data-testid={`provider-slot-link-${provider.id}-${label}`}
              className="inline-flex items-center gap-1 text-xs text-primary disabled:text-muted-foreground">
              <Link2 className="w-3.5 h-3.5" /> {doc?.url ? "Modifier le lien" : "Utiliser un lien"}
            </button>
          )}
          {doc && (
            <button type="button" onClick={remove} disabled={busy} className="text-xs text-muted-foreground hover:text-destructive"
              data-testid={`provider-slot-remove-${provider.id}-${label}`}>Retirer</button>
          )}
          {link !== null && (
            <form className="basis-full flex flex-wrap items-center gap-2 pl-12" onSubmit={(e) => { e.preventDefault(); saveLink(); }}>
              <Input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" autoFocus
                aria-label={`Lien vers les ${label} de ${provider.name}`} data-testid={`provider-slot-link-input-${provider.id}-${label}`}
                className="h-8 flex-1 min-w-[220px] rounded-sm text-xs" />
              <Button type="submit" size="sm" disabled={busy || !link.trim()} data-testid={`provider-slot-link-save-${provider.id}-${label}`}
                className="h-8 rounded-sm bg-primary hover:bg-primary/90 text-xs">Enregistrer</Button>
              <button type="button" onClick={() => setLink(null)} className="text-xs text-muted-foreground">Annuler</button>
            </form>
          )}
        </>
      )}
    </div>
  );
}

function ProviderDialog({ provider, onClose, onSaved }) {
  const [form, setForm] = useState({ name: provider?.name || "", note: provider?.note || "" });
  const [docs, setDocs] = useState(provider?.documents?.map(docPayload) || []);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!label.trim()) return toast.error("Indiquez d'abord le libellé du document (ex. CGU)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { label: label.trim(), file_id: data.id }]);
      setLabel("");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), note: form.note.trim() || null, documents: docs };
      if (provider) await api.put(`/catalog/providers/${provider.id}`, payload);
      else await api.post("/catalog/providers", payload);
      toast.success(provider ? "Prestataire mis à jour" : "Prestataire ajouté");
      onSaved();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="provider-dialog">
        <DialogHeader><DialogTitle className="font-heading">{provider ? "Modifier le prestataire" : "Ajouter un prestataire"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="provider-name">Nom</Label>
            <Input id="provider-name" value={form.name} placeholder="NEXTforTV" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="provider-name" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label htmlFor="provider-note">Mention affichée au client (optionnel)</Label>
            <Textarea id="provider-note" rows={2} value={form.note} placeholder="Offre dissociée du forfait internet."
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="provider-note" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <p className="text-sm font-medium">Documents à accepter</p>
            <div className="space-y-1.5 mt-2">
              {docs.length === 0 && <p className="text-xs text-muted-foreground">Aucun document.</p>}
              {docs.map((d, i) => (
                <div key={d.file_id || d.url} className="flex items-center justify-between gap-2 border border-border rounded-sm px-2.5 py-1.5 text-sm" data-testid={`provider-doc-row-${i}`}>
                  <a href={providerDocHref(d) || undefined} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-primary truncate">
                    {d.url ? <ExternalLink className="w-3.5 h-3.5 text-primary shrink-0" /> : <FileText className="w-3.5 h-3.5 text-primary shrink-0" />} {d.label}
                    {d.url && <span className="text-xs text-muted-foreground truncate">({d.url.replace(/^https:\/\//i, "")})</span>}
                  </a>
                  <button type="button" onClick={() => setDocs((all) => all.filter((_, j) => j !== i))} title="Retirer" className="text-muted-foreground hover:text-destructive">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {docs.length < 5 && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé (CGU, CGV…)" data-testid="provider-doc-label" className="h-9 w-40 rounded-sm" />
                <label className={`flex items-center gap-1.5 text-sm cursor-pointer ${label.trim() ? "text-primary" : "text-muted-foreground"}`}>
                  <Upload className="w-4 h-4" /> {uploading ? "Envoi…" : "Ajouter le PDF"}
                  <input type="file" accept="application/pdf,.pdf" className="sr-only" data-testid="provider-doc-input" disabled={uploading}
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Annuler</Button>
          <Button onClick={save} disabled={saving || uploading || !form.name.trim()} data-testid="provider-save" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelsTab({ canManage, onOpenProviders }) {
  const [channels, setChannels] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [providers, setProviders] = useState([]);
  useEffect(() => { api.get("/catalog/providers").then((r) => setProviders(r.data)).catch(() => setProviders([])); }, []);
  const [editing, setEditing] = useState(undefined); // undefined = fermé, null = ajout, objet = modification
  const load = () => api.get("/catalog/channels", { params: { include_archived: 1 } })
    .then((r) => setChannels(r.data)).catch((e) => toast.error(formatAxiosError(e)));
  useEffect(() => { load(); }, []);
  if (!channels) return <p className="text-muted-foreground">Chargement…</p>;

  const groups = [...new Set(channels.map((c) => c.group))];
  const groupEn = (g) => channels.find((c) => c.group === g && c.group_en)?.group_en;
  const active = channels.filter((c) => c.active);
  return (
    <div className="space-y-6" data-testid="channels-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="channels-summary">
          {active.length} chaînes actives : {active.filter((c) => c.tier === "core").length} dans le cœur, {active.filter((c) => c.tier === "choice").length} au choix, {active.filter((c) => c.tier === "all").length} All NEXT uniquement · {active.filter((c) => c.logo_file_id).length} avec logo · {active.filter((c) => c.dual_audio).length} en double audio (EN/ES)
          {active.some((c) => c.coming_soon) && ` · ${active.filter((c) => c.coming_soon).length} bientôt disponible${active.filter((c) => c.coming_soon).length > 1 ? "s" : ""} (pas encore au choix)`}
        </p>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setTranslating(true)} data-testid="translate-channel-groups" className="rounded-sm">
              <Languages className="w-4 h-4 mr-1" /> Catégories en anglais
            </Button>
            <Button onClick={() => setEditing(null)} data-testid="new-channel-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Ajouter une chaîne
            </Button>
          </div>
        )}
      </div>
      {translating && (
        <TranslationDialog title="Catégories de chaînes en anglais" onClose={() => setTranslating(false)}
          pairs={groups.map((g) => ({ key: g, fr: g, en: groupEn(g) }))}
          onSave={async (translations) => { await api.put("/catalog/channel-groups", { translations }); load(); }} />
      )}
      {providers.map((pr) => {
        const has = (label) => pr.documents.some((d) => d.label.trim().toLowerCase() === label.toLowerCase());
        const missing = PROVIDER_DOC_SLOTS.filter((l) => !has(l));
        return (
          <div key={pr.id} data-testid={`channels-provider-${pr.id}`}
            className={`flex flex-wrap items-center justify-between gap-3 border rounded-sm px-4 py-3 text-sm ${missing.length ? "border-amber-300 bg-amber-50" : "border-border bg-card"}`}>
            <p>
              <span className="font-medium">Documents {pr.name}</span> (acceptés par le client à la signature) :{" "}
              {PROVIDER_DOC_SLOTS.map((l) => `${l} ${has(l) ? "✓" : "manquantes"}`).join(" · ")}
            </p>
            {onOpenProviders && (
              <Button variant="outline" size="sm" onClick={onOpenProviders} className="rounded-sm" data-testid={`channels-provider-manage-${pr.id}`}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Gérer les CGU / CGV
              </Button>
            )}
          </div>
        );
      })}
      {groups.map((g) => (
        <div key={g}>
          <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">
            {g}{groupEn(g) && <span className="normal-case tracking-normal font-normal"> · {groupEn(g)}</span>}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {channels.filter((c) => c.group === g).map((c) => (
              <div key={c.id} data-testid={`channel-row-${c.id}`}
                className={`flex items-center gap-3 border border-border rounded-sm p-2 bg-card ${c.active ? "" : "opacity-50"}`}>
                <div className="w-16 aspect-[2/1] bg-black rounded-sm flex items-center justify-center overflow-hidden shrink-0">
                  {c.logo_file_id
                    ? <img src={fileUrl(c.logo_file_id)} alt="" className="w-full h-full object-contain" loading="lazy" />
                    : <span className="text-white text-[9px] font-semibold text-center px-1 leading-tight">{c.name}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-[11px] text-muted-foreground">{[TIER_LABELS[c.tier], c.dual_audio && "EN/ES", c.coming_soon && "Bientôt disponible", !c.active && "Archivée"].filter(Boolean).join(" · ") || "\u00a0"}</p>
                </div>
                {canManage && (
                  <button onClick={() => setEditing(c)} title="Modifier" data-testid={`edit-channel-${c.id}`} className="text-muted-foreground hover:text-primary">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {editing !== undefined && (
        <ChannelDialog channel={editing} groups={groups} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function ChannelDialog({ channel, groups, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: channel?.name || "", group: channel?.group || groups[0] || "", dual_audio: !!channel?.dual_audio, tier: channel?.tier || "choice",
    active: channel ? channel.active : true, logo_file_id: channel?.logo_file_id || null, coming_soon: !!channel?.coming_soon,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_file_id: data.id }));
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), group: form.group.trim(), dual_audio: form.dual_audio, logo_file_id: form.logo_file_id, tier: form.tier, coming_soon: form.coming_soon };
      if (channel) await api.put(`/catalog/channels/${channel.id}`, { ...payload, active: form.active });
      else await api.post("/catalog/channels", payload);
      toast.success(channel ? "Chaîne mise à jour" : "Chaîne ajoutée");
      onSaved();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="channel-dialog">
        <DialogHeader><DialogTitle className="font-heading">{channel ? "Modifier la chaîne" : "Ajouter une chaîne"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="channel-name">Nom</Label>
            <Input id="channel-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="channel-name" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label htmlFor="channel-group">Groupe</Label>
            <Input id="channel-group" list="channel-groups" value={form.group} onChange={(e) => setForm((f) => ({ ...f, group: e.target.value }))} data-testid="channel-group" className="mt-1.5 rounded-sm" />
            <datalist id="channel-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-24 aspect-[2/1] bg-black rounded-sm flex items-center justify-center overflow-hidden shrink-0" data-testid="channel-logo-preview">
              {form.logo_file_id
                ? <img src={fileUrl(form.logo_file_id)} alt="Logo de la chaîne" className="w-full h-full object-contain" />
                : <span className="text-white text-[10px]">Sans logo</span>}
            </div>
            <label className="flex items-center gap-1.5 text-sm text-primary cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading ? "Envoi…" : "Logo (PNG/JPEG, fond noir conseillé)"}
              <input type="file" accept="image/png,image/jpeg" className="sr-only" data-testid="channel-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            {form.logo_file_id && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, logo_file_id: null }))} className="text-xs text-muted-foreground underline">Retirer</button>
            )}
          </div>
          <div>
            <Label htmlFor="channel-tier">Niveau</Label>
            <select id="channel-tier" value={form.tier} data-testid="channel-tier" onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
              className="mt-1.5 h-9 w-full border border-border rounded-sm px-2 text-sm bg-background">
              {Object.entries(TIER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.dual_audio} onCheckedChange={(v) => setForm((f) => ({ ...f, dual_audio: v }))} data-testid="channel-dual-audio" /> Double audio anglais / espagnol
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.coming_soon} onCheckedChange={(v) => setForm((f) => ({ ...f, coming_soon: v }))} data-testid="channel-coming-soon" />
            Bientôt disponible (affichée dans les offres, pas encore au choix)
          </label>
          {channel && (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="channel-active" /> Proposée dans les offres (décocher pour archiver)
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Annuler</Button>
          <Button onClick={save} disabled={saving || uploading || !form.name.trim() || !form.group.trim()} data-testid="channel-save" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

